import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Element } from 'react-scroll';
import { FormattedMessage, useIntl } from 'react-intl';
import { always, isEmpty } from 'ramda';
import { useRouter } from 'next/router';
import { useInView } from 'react-intersection-observer';
import { saveAs } from 'file-saver';
import Konva from 'konva';
import dayjs from 'dayjs';
import { useAsync } from 'react-use';

import { Artwork } from '@definitions/artworks.types';
import { ArtworkSpecs } from '@components/PDP/artworkSpecs';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';
import { useMonogram } from '@hooks/useMonogram';
import { COLORS } from '@theme/colors';
import { getFullArtistName, getShowAddToBag } from '@utils/helpers';
import { EXPERIMENT_ID, ROUTE } from '@utils/constants';
import { HEADER_TOP } from '@components/header/header.constants';
import { Layout } from '@components/layout';
import { useSetArtworkDynamicData } from '@hooks/useArtworkDynamicData';
import { getArtworkDynamicData } from '@modules/plp/plp.helpers';
import { KlarnaPlacement } from '@components/klarnaPlacement';
import { useArtworkSpaceView } from '@hooks/useArtworkSpaceView';
import { useBreakpoint } from '@hooks/useBreakpoint';
import { MOBILE } from '@theme/media';
import { MobileArtworkSpaceView } from '@components/PDP/mobileArtworkSpaceView';
import { useTrackConversions } from '@hooks/useTrackConversions';
import { CONVERSIONS_EVENTS, VIEW_CONTENT_TYPES } from '@definitions/conversions.types';
import { TalkToExpert } from '@components/PDP/talkToExpert';
import { useGoogleOptimize } from '@hooks/useGoogleOptimize';
import { OfferedIn } from '@components/offeredIn';
import { UnitCountDown } from '@components/unitCountDown';
import { useSellingState } from '@hooks/useArtworks/useArtworks';

import { ArtworkSidebar } from '../artworkSidebar';
import { RecommendedArtworks } from '../recommendedArtworks';
import { ArtworkArtist } from '../artworkArtist';
import { ArtworkImages } from '../artworkImages';
import { ArtworkDescription } from '../artworkDescription';
import { ArtworkDetails } from '../artworkDetails';
import { ARTISTS_SCROLL_LINK, DETAILS_SCROLL_LINK, IMAGE_SCROLL_LINK } from '../pdp.constants';
import { ArtworkGallery } from '../artworkGallery';
import { ArtworkShow } from '../artworkShow';
import { ArtworkSpaceView } from '../artworkSpaceView';
import { ExpiringBlock } from '../expiringBlock';
import { ArtworkDetailsMobile } from '../artworkDetailsMobile';

import {
  ArtworkImagesWrapper,
  ArtworkShortDetails,
  Bullet,
  BulletPoints,
  Container,
  Content,
  DetailsDescription,
  DetailsTitle,
  KlarnaContainer,
  MasterEditorial,
  MobileDescriptionBlock,
  ArtworkDetailsWrapper,
  OfferedInMobileContainer,
} from './artworkView.styles';

export interface ArtworkViewProps {
  artwork: Artwork;
  variantId?: string;
}

export const ArtworkView = ({ artwork, variantId }: ArtworkViewProps) => {
  const { addMonogram, clearMonogram } = useMonogram();
  const [imagesRef, imagesInView] = useInView({ initialInView: true });
  const { artworkDynamicData, setArtworkDynamicData } = useSetArtworkDynamicData();
  const artworkData = { ...artwork, ...(artworkDynamicData as object) };
  const [backgroundColor, setBackgroundColor] = useState(COLORS.WHITE);
  const renderSpecs = renderWhenTrue(always(<ArtworkSpecs artwork={artworkData} />));
  const renderShows = renderWhenTrue(always(<ArtworkShow shows={artworkData.shows} />));
  const intl = useIntl();
  const artworkDetailsRef = useRef<HTMLDivElement>(null);
  const { breakpoint } = useBreakpoint();
  const { sendConversion } = useTrackConversions();
  const isMobile = breakpoint === MOBILE;
  const router = useRouter();
  const {
    isSpaceViewMode,
    toggleSpaceViewMode,
    closeSpaceViewMode,
    openSpaceViewMode,
    backgroundImage,
    setBackgroundImage,
    measure,
    setMeasure,
    spaceViewStep,
    setSpaceViewStep,
    distance,
    setDistance,
  } = useArtworkSpaceView();
  const downloadImageRef = useRef<Konva.Stage | null>(null);
  const [isExclusive] = useState(Boolean(router.query.exclusive));
  const [isShoppable] = useState(router.query.isShoppable === 'true');
  const isDesktopSpaceView = isSpaceViewMode && !isMobile;
  const isMobileSpaceView = isSpaceViewMode && isMobile;
  const artist = getFullArtistName(artworkData.artist);
  const headerTitle = `${artworkData.title} ${artist} | Buy Art Online`;
  const { isWithinSellingDates } = useSellingState(artwork);
  const headerDescription = intl.formatMessage(
    {
      id: 'artworks.headerDescription',
      defaultMessage: "Check out {artist}'s artwork, {title}, alongside other new artworks to buy now on Platform.",
    },
    {
      artist,
      title: artworkData.title,
    }
  );
  const { dynamicData, defaultVariant } = getArtworkDynamicData(artwork, variantId);
  const isBeforeOneMonth = dayjs(artwork.liveEndAt).isBefore(dayjs().utc().add(30, 'day'));
  const variant = useGoogleOptimize(EXPERIMENT_ID);
  const { isPostSellingDate } = useSellingState(artworkData);

  const renderExperimentSendEvent = renderWhenTrue(
    always(
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
            gtag('event', 'experiment_impression', {
              'experiment_id': '${EXPERIMENT_ID}',
              'variant_id': '${EXPERIMENT_ID}.${variant || ''}',
              'send_to': 'GA_MEASUREMENT_ID',
            });
            `,
        }}
      />
    )
  );

  useEffect(() => {
    setArtworkDynamicData(dynamicData);
  }, [artwork.bcId]);

  useEffect(() => {
    if (isMobile) {
      closeSpaceViewMode();
    }
  }, [breakpoint]);

  useEffect(() => {
    if (isSpaceViewMode) {
      setBackgroundColor(COLORS.WHITE);
    }
  }, [isSpaceViewMode]);
  useAsync(async () => {
    setArtworkDynamicData(dynamicData);

    const id = new Date().getTime();
    const omnisendProductViewed = {
      $productID: artworkData.bcId,
      $variantID: defaultVariant?.bigCommerceId || (artworkData.baseVariantId && artworkData.baseVariantId.toString()),
      $currency: 'USD',
      $price: (artworkData.price || 0) * 100,
      $title: artworkData.title,
      $description: getFullArtistName(artworkData.artist),
      $imageUrl: artworkData.primaryImage?.urlStandard,
      $productUrl: `https://platformart.com${ROUTE.ARTWORKS}/${artworkData.artist.slug}/${artworkData.slug}`,
    };

    (window as any).omnisend = (window as any).omnisend || [];
    (window as any).omnisend.push(['track', '$productViewed', omnisendProductViewed]);

    if (artworkData.monogram?.monogramLetter?.length) {
      const { monogramLetter, monogramColor, monogramAnimationType } = artworkData.monogram;

      addMonogram({
        letter: monogramLetter,
        color: monogramColor,
        appearance: monogramAnimationType,
        id,
      });
    }

    await sendConversion({
      eventType: CONVERSIONS_EVENTS.VIEW_CONTENT,
      contentName: VIEW_CONTENT_TYPES.PDP,
    });

    return () => {
      clearMonogram(id);
    };
  }, [variantId]);
  const isInStock = getShowAddToBag({ ...artwork, isExclusive, isShoppable });

  const microDataTags = [
    { property: 'product:brand', content: getFullArtistName(artworkData.artist) },
    { property: 'product:availability', content: isInStock ? 'in stock' : 'out of stock' },
    { property: 'product:condition', content: 'new' },
    { property: 'product:price:amount', content: `${artworkData.price}` },
    { property: 'product:price:currency', content: 'USD' },
    { property: 'product:retailer_item_id', content: `${artworkData.bcId}` },
  ];

  const createImageAndDownload = () => {
    const downloadImage = downloadImageRef.current;

    if (downloadImage) {
      setTimeout(() => {
        saveAs(downloadImage.toDataURL({ pixelRatio: 3 }), `${artwork.title} in Room.jpeg`);
      });
    }
  };

  const renderTalkToExpert = renderWhenTrue(
    always(<TalkToExpert artist={artworkData.artist} title={artworkData.title} />)
  );

  const renderMobileExpiringBlock = renderWhenTrue(() => (
    <ExpiringBlock isMobileVersion liveEndAt={artwork.liveEndAt} />
  ));

  const renderMobileUnitCountDown = renderWhenTrue(() => (
    <UnitCountDown inventoryLevel={artwork.inventoryLevel} withIcon isMobileVersion />
  ));

  const renderWhenOffered = renderWhenTrue(() => (
    <OfferedInMobileContainer>
      <OfferedIn liveEndAt={artwork.liveEndAt} />
    </OfferedInMobileContainer>
  ));

  const renderArtworkBody = () => (
    <Fragment>
      <MasterEditorial>
        <div style={{ marginTop: artworkData.category.toLowerCase() === 'products' ? 40 : 0 }}>
          <ArtworkShortDetails noPadding={true}>{renderTalkToExpert(isMobile)}</ArtworkShortDetails>
        </div>
        <ArtworkShortDetails>
          <DetailsTitle>
            <FormattedMessage id="ArtworkView.details" defaultMessage="Details:" />
          </DetailsTitle>
          <DetailsDescription>{artworkData.description.shortDescription}</DetailsDescription>
          <BulletPoints>
            {artworkData.description.bulletPoints
              ?.map((bullet, index) => {
                if (
                  (artworkData.category.toLowerCase() === 'products' ||
                    artworkData.subcategory.toLowerCase() === 'sculptures') &&
                  (bullet.toLowerCase().includes('framed') || bullet.toLowerCase().includes('unframed'))
                ) {
                  return null;
                }
                return <Bullet key={index}>{bullet}</Bullet>;
              })
              .filter(Boolean)}
          </BulletPoints>
          <KlarnaContainer
            style={{
              marginTop: 30,
              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
            }}
          >
            <KlarnaPlacement typeOfDataKey="top-strip-promotion-auto-size" price={artwork.price} />
          </KlarnaContainer>
        </ArtworkShortDetails>
        {renderMobileExpiringBlock(isBeforeOneMonth && isInStock)}
        {renderMobileUnitCountDown(
          artwork.inventoryLevel > 0 && isWithinSellingDates && artwork.inventoryLevel <= artwork.lowInventoryLevel
        )}
        {renderWhenOffered(isPostSellingDate && !isExclusive && isMobile)}
        <Element name={DETAILS_SCROLL_LINK}>
          <ArtworkDescription description={artworkData.description} title={artworkData.title} />
          {renderSpecs(artworkData.showSpecs)}
        </Element>
        <ArtworkGallery gallery={artworkData.gallery} />
        <Element name={ARTISTS_SCROLL_LINK}>
          <ArtworkArtist
            artist={artworkData.artist}
            additionalArtistBiography={artworkData.additionalArtistBiography}
          />
        </Element>
        {renderShows(!isEmpty(artworkData.shows))}
      </MasterEditorial>
      <MobileDescriptionBlock showBackdropFilter={isMobileSpaceView}>
        <ArtworkDetailsWrapper isSpaceViewMode={isSpaceViewMode}>
          <ArtworkDetailsMobile
            artwork={artworkData}
            defaultVariant={defaultVariant}
            innerRef={artworkDetailsRef}
            showBackground={!isMobileSpaceView}
          />
        </ArtworkDetailsWrapper>
        <MobileArtworkSpaceView
          showSpaceView={isMobileSpaceView}
          closeSpaceView={closeSpaceViewMode}
          detailViewHeight={artworkDetailsRef.current?.offsetHeight || 0}
          artwork={artwork}
          toggleSpaceViewMode={toggleSpaceViewMode}
          backgroundImage={backgroundImage}
          setBackgroundImage={setBackgroundImage}
          measure={measure}
          setMeasure={setMeasure}
          spaceViewStep={spaceViewStep}
          setSpaceViewStep={setSpaceViewStep}
          distance={distance}
          setDistance={setDistance}
          downloadImage={createImageAndDownload}
          innerRef={downloadImageRef}
        />
      </MobileDescriptionBlock>
    </Fragment>
  );

  const renderArtworkSpaceView = () => (
    <ArtworkSpaceView
      toggleSpaceViewMode={toggleSpaceViewMode}
      artwork={artworkData}
      backgroundImage={backgroundImage}
      measure={measure}
      distance={distance}
      setDistance={setDistance}
      spaceViewStep={spaceViewStep}
      ref={downloadImageRef}
    />
  );

  const renderRecommendedArtworks = () => <RecommendedArtworks artworks={artworkData.recommendedArtworks} />;

  return (
    <Layout
      title={headerTitle}
      defaultDescription={headerDescription}
      headerTop={HEADER_TOP.PDP}
      seo={artworkData.seo}
      microDataTags={microDataTags}
    >
      <Container>
        <Content backgroundColor={backgroundColor} isSpaceViewMode={isSpaceViewMode}>
          <div>
            <ArtworkImagesWrapper isSpaceView={isDesktopSpaceView} ref={imagesRef}>
              <Element name={IMAGE_SCROLL_LINK}>
                <ArtworkImages
                  artwork={artworkData}
                  images={artworkData.images}
                  urlStandard={artworkData.urlStandard}
                  isSpaceViewMode={isDesktopSpaceView}
                  spaceViewStep={spaceViewStep}
                  hideImage={!!measure && !!distance}
                  openSpaceViewMode={openSpaceViewMode}
                  mobileDetailsHeight={artworkDetailsRef.current?.offsetHeight}
                />
              </Element>
            </ArtworkImagesWrapper>
            {renderWhenTrueOtherwise(renderArtworkSpaceView, renderArtworkBody)(isDesktopSpaceView)}
          </div>
          <ArtworkSidebar
            artwork={artworkData}
            setBackgroundColor={setBackgroundColor}
            defaultVariant={defaultVariant}
            toggleSpaceViewMode={toggleSpaceViewMode}
            openSpaceViewMode={openSpaceViewMode}
            isSpaceViewMode={isSpaceViewMode}
            setBackgroundImage={setBackgroundImage}
            backgroundImage={backgroundImage}
            measure={measure}
            setMeasure={setMeasure}
            spaceViewStep={spaceViewStep}
            setSpaceViewStep={setSpaceViewStep}
            downloadImage={createImageAndDownload}
            distance={distance.value}
            imagesInView={imagesInView}
            isInStock={isInStock}
          />
        </Content>
        {renderWhenTrue(renderRecommendedArtworks)(!isSpaceViewMode)}
      </Container>
      {renderExperimentSendEvent(EXPERIMENT_ID)}
    </Layout>
  );
};
